import { zodResolver } from "@hookform/resolvers/zod";
import LoadingButton from "@mui/lab/LoadingButton";
import { Divider, Grid, TextField, Typography, Box, InputAdornment, IconButton } from "@mui/material";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { connectedCompanyPersonFormSchema } from "../../components/dialog/AddConnectedCompanyPerson/AddConnectedCompanyPerson";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { COMPANY_LEGAL_TYPES, FORM_ERR } from "../../helpers/constants";
import { useToasts } from "../../hooks/useToasts";
import { useAddCompanyMutation } from "../../services/customersService";
import { CompanyAddConnectedPersons } from "./Passport/CompanyAddConnectedPersons";
import { AddressCopyButton } from "../../components/buttons/AddressCopyButton";

import { useDialogBetter } from "../../hooks/useDialogBetter";
import { AdditionalPhonesDialog } from "./Passport/AdditionalPhonesDialog/AdditionalPhonesDialog";
import { AdditionalEmailsDialog } from "./Passport/AdditionalEmailsDialog/AdditionalEmailsDialog";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MailIcon from "@mui/icons-material/Mail";

import { useForm as useFromBasic  } from "../../hooks/useForm";

export const counterpartyPassportFormSchema = z
    .object({
        // Dane podstawowe
        companyName: z.string().nonempty({ message: FORM_ERR.name_required }),
        companyShortName: z.string().nonempty({ message: "short_name_field_required" }),
        nip: z.string().optional(),
        regon: z.string().optional(),
        email: z.preprocess(
            (val) => (val === "" ? undefined : val),
            z.string().email(t("form.validations.incorrect_email")).optional().nullable(),
        ),
        phone: z.string().optional(),
        companyType: z.enum(COMPANY_LEGAL_TYPES, {
            message: "company_legal_type_required",
        }),

        // Company address
        street: z.string().optional(),
        houseNumber: z.string().optional(),
        apartmentNumber: z.string().optional(),
        city: z.string().optional(),
        postalCode: z.string().optional(),
        country: z.string().optional(),

        // Company mailing address
        streetMailing: z.string().optional(),
        houseNumberMailing: z.string().optional(),
        apartmentNumberMailing: z.string().optional(),
        cityMailing: z.string().optional(),
        postalCodeMailing: z.string().optional(),
        countryMailing: z.string().optional(),

        // Connected persons
        connectedPersons: z.array(connectedCompanyPersonFormSchema).default([]),
        additionalEmails: z.array().optional(),
        additionalPhones: z.array().optional(),

        note: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.country === "Polska") {
            const phoneRegex = /^\+48\s?\d{3}\s?\d{3}\s?\d{3}$/;
            if (data.phone && !phoneRegex.test(data.phone)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_phone"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const nipRegex = /^\d{10}$/;
            if (data.nip && !nipRegex.test(data.nip)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_nip"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const regonRegex = /^\d{9}$/;
            if (data.regon && !regonRegex.test(data.regon)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_regon"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const zipCodeRegex = /^\d{2}-\d{3}$/;
            if (data.postalCode && !zipCodeRegex.test(data.postalCode)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }
            if (data.postalCodeMailing && !zipCodeRegex.test(data.postalCodeMailing)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }
        }
        return z.NEVER;
    });

export function CustomerAddCompanyDialog(props) {
    const { handleSubmit, control, formState, register, reset, setValue, getValues, watch } =
        useForm({
            resolver: zodResolver(counterpartyPassportFormSchema),
            defaultValues: {},
        });

    const { mutate: addCompany, isPending } = useAddCompanyMutation();
    const { error, onFormErrors } = useToasts();

  const { formValue: additionalPhones, setFormValue: setAdditionalPhones } =
    useFromBasic([""]);
  const { formValue: additionalEmails, setFormValue: setAdditionalEmails } =
    useFromBasic([""]);

  const { openDialog: openAdditionalPhonesDialog } = useDialogBetter();
  const { openDialog: openAdditionalEmailsDialog } = useDialogBetter();

    const enabledBtn = watch([
        "companyName",
        "companyShortName",
        "companyType",
        "nip",
        "email",
    ]).reduce((acc, val) => acc && !!val, true);

    const onSubmit = (data) => {
        console.log(data);
        addCompany(
            {
                building: data.houseNumber || "",
                city: data.city || "",
                email: data.email || "",
                flat_nr: data.apartmentNumber || "",
                name: data.companyName || "",
                name_short: data.companyShortName || "",
                legal: data.companyType || "",
                nip: data.nip || "",
                phone: data.phone || "",
                note: data.note || "",
                regon: data.regon || "",
                street: data.street || "",
                zip_code: data.postalCode || "",
                additional_emails: additionalEmails.filter((email) => email),
                additional_phones: additionalPhones.filter((phone) => phone),
                mailing_address : (
                    data.cityMailing || 
                    data.streetMailing || 
                    data.houseNumberMailing || 
                    data.apartmentNumberMailing || 
                    data.postalCodeMailing
                ) ? {
                    street: data.streetMailing || "",
                    building: data.houseNumberMailing || "",
                    flat_nr: data.apartmentNumberMailing || "",
                    city: data.cityMailing || "",
                    zip_code: data.postalCodeMailing || "",
                } : null,
                related_persons: data.connectedPersons.map((person) => {
                    if (person.exisitngClientPersonId) {
                        return {
                            person_id: person.exisitngClientPersonId,
                            role: person.companyFunction,
                        };
                    }
                    return {
                        role: person.companyFunction,
                        person: {
                            email: {
                                email_address: person.email || "",
                            },
                            phone: {
                                phone_number: person.phone || "",
                            },
                            person_name: person.name || "",
                            person_surname: person.surname || "",
                        },
                    };
                }),
            },
            {
                onError: (v) => error(v.message),
                onSuccess: () => {
                    toast.custom(
                        () => (
                            <SnackbarSuccess
                                message={t("clients.customerAddCompanyDialog.companyAdded")}
                            />
                        ),
                        {
                            position: "bottom-right",
                        },
                    );
                    props.onClose();
                },
            },
        );
    };

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("clients.customerAddCompanyDialog.title")}
            maxWidth={"xl"}
            showDialogActions
            headerComponent={
                <Typography
                    variant={"subtitle1"}
                    sx={{ color: "text.secondary", mx: "auto", mb: 1 }}
                >
                    {t("clients.customerAddCompanyDialog.subtitle")}
                </Typography>
            }
            titleSx={{
                pb: 0,
            }}
        >
            <Grid container direction={"row"}>
                <Grid container direction={"column"} xs>
                    <Grid container direction={"row"} xs>
                        <Grid container direction={"column"} xs>
                            <Typography variant={"h6"} sx={{ mb: 2 }}>
                                {t("clients.passportDialogCompany.basicDataFormTitle")}
                            </Typography>
                            <Grid container direction={"column"} spacing={2}>
                                <Grid item>
                                    <TextField
                                        {...register("companyName")}
                                        label={t("clients.customerAddCompanyDialog.companyName")}
                                        fullWidth
                                        size="small"
                                        onChange={(e)=> {
                                            setValue("companyShortName", e.target.value)
                                            register("companyName")['onChange'](e)
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("companyShortName")}
                                        label={t(
                                            "clients.customerAddCompanyDialog.companyShortName",
                                        )}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{shrink: Boolean(watch("companyName")) || Boolean(watch("companyShortName"))}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("nip")}
                                        label={t("clients.customerAddCompanyDialog.nip")}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("regon")}
                                        label={t("clients.customerAddCompanyDialog.regon")}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("email")}
                                        label={t("clients.passportDialogCompany.primary_email")}
                                        fullWidth
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                    openAdditionalEmailsDialog({
                                                        Component: AdditionalEmailsDialog,
                                                        componentProps: {
                                                        personId: null,
                                                        formValue: additionalEmails.map(
                                                            (email) => ({ email_address: email }),
                                                        ),
                                                        onSetFormValue: (emails) =>
                                                            setAdditionalEmails(emails),
                                                        },
                                                    })
                                                    }
                                                >
                                                    <MailIcon/>
                                                </IconButton>
                                                </InputAdornment>
                                            )
                                        }}   
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("phone")}
                                        label={t("clients.passportDialogCompany.primary_phone")}
                                        fullWidth
                                        size="small"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                    openAdditionalPhonesDialog({
                                                        Component: AdditionalPhonesDialog,
                                                        componentProps: {
                                                        formValue: additionalPhones.map(
                                                            (phone) => ({ phone_number: phone }),
                                                        ),
                                                        onSetFormValue: (phones) =>
                                                            setAdditionalPhones(phones),
                                                        },
                                                    })
                                                    }
                                                >
                                                    <LocalPhoneIcon/>
                                                </IconButton>
                                                </InputAdornment>
                                            )
                                        }}                            
                                    />
                                </Grid>
                                <Grid item>
                                    <ControlledSelect
                                        control={control}
                                        label={t("clients.customerAddCompanyDialog.companyType")}
                                        name="companyType"
                                        data={COMPANY_LEGAL_TYPES.map((val) => ({
                                            label: val,
                                            value: val,
                                        }))}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                        
                        <Grid container direction={"column"} xs>
                            <Box sx={{ display: "flex", alignItems: "center",justifyContent: "center" }}>
                                <Typography variant={"h6"} sx={{ mb: 2 }}>
                                    {t("clients.passportDialogCompany.addressFormTitle")}
                                </Typography>
                                <AddressCopyButton
                                    form={{ watch, setValue }}
                                    sourcePrefix=""
                                    targetSuffix="Mailing"
                                    fields={[
                                        "street",
                                        "houseNumber",
                                        "apartmentNumber",
                                        "city",
                                        "postalCode",
                                        "country",
                                    ]}
                                    tooltipText={t("clients.passportDialogCompany.copyAddress")}
                                    sx={{ ml: 1, mb: 2 }}
                                />
                            </Box>
                            <Grid container direction={"column"} spacing={2}>
                                <Grid item>
                                    <TextField
                                        {...register("street")}
                                        fullWidth
                                        label={t("clients.customerAddCompanyDialog.street")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("houseNumber")}
                                        fullWidth
                                        label={t("clients.customerAddCompanyDialog.buildingNumber")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("apartmentNumber")}
                                        fullWidth
                                        label={t(
                                            "clients.customerAddCompanyDialog.apartmentNumber",
                                        )}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("city")}
                                        fullWidth
                                        label={t("clients.customerAddCompanyDialog.city")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("postalCode")}
                                        fullWidth
                                        label={t("clients.customerAddCompanyDialog.postalCode")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("country")}
                                        fullWidth
                                        label={t("clients.customerAddCompanyDialog.country")}
                                        size="small"
                                        defaultValue={t(
                                            "clients.customerAddCompanyDialog.defaultCountry",
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                        <Grid container direction={"column"} xs>
                            <Typography variant={"h6"} sx={{ mb: 2 }}>
                                {t("clients.passportDialogCompany.addressMailingFormTitle")}
                            </Typography>
                                <Grid container direction={"column"} spacing={2}>
                                    <Grid item>
                                        <TextField
                                            {...register("streetMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.street")}
                                            size="small"
                                            InputLabelProps={{shrink: watch("streetMailing")}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            {...register("houseNumberMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.buildingNumber")}
                                            size="small"
                                            InputLabelProps={{shrink: watch("houseNumberMailing")}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            {...register("apartmentNumberMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.apartmentNumber")}
                                            size="small"
                                            InputLabelProps={{shrink: watch("apartmentNumberMailing")}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            {...register("cityMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.city")}
                                            size="small"
                                            InputLabelProps={{shrink: watch("cityMailing")}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            {...register("postalCodeMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.postalCode")}
                                            size="small"
                                            InputLabelProps={{shrink: watch("postalCodeMailing")}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            {...register("countryMailing")}
                                            fullWidth
                                            label={t("clients.passportDialogCompany.country")}
                                            size="small"
                                            defaultValue="Polska"
                                            InputLabelProps={{shrink: watch("countryMailing")}}
                                        />
                                    </Grid>
                                </Grid>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                </Grid>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Grid container direction={"column"} item xs={5}>
                    <Grid container direction={"column"}>
                        <CompanyAddConnectedPersons
                            // @ts-ignore
                            control={control}
                            // @ts-ignore
                            setValue={setValue}
                        />
                        <Divider sx={{ my: 2 }} />
                        <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.customerAddCompanyDialog.additionalInfo")}
                        </Typography>
                        <Typography
                            variant={"body1"}
                            sx={{ fontWeight: "medium", textAlign: "start" }}
                        >
                            {t("clients.customerAddCompanyDialog.companyNote")}
                        </Typography>
                        <Grid container justifyContent={"start"}>
                            <TextField
                                {...register("note")}
                                fullWidth
                                label={t("clients.customerAddCompanyDialog.note")}
                                InputProps={{
                                    rows: 3,
                                    multiline: true,
                                    inputComponent: "textarea",
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <LoadingButton
                disabled={!enabledBtn}
                loading={isPending}
                variant="contained"
                fullWidth
                sx={{ mt: 3 }}
                onClick={handleSubmit(onSubmit, onFormErrors)}
            >
                {t("clients.customerAddCompanyDialog.add")}
            </LoadingButton>
        </BasicDialog>
    );
}
